import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import { getDateKeyMexicoCity, formatDateTimeMexicoCity } from '@/lib/dateUtils';
import type { BehavioralNote, BehavioralNoteType } from '@/types';
import { ChevronLeft, Smile, Meh, Frown, RefreshCw } from 'lucide-react';

const TYPE_CONFIG: Record<BehavioralNoteType, { label: string; icon: typeof Smile; colorClass: string }> = {
  positive: { label: 'Bien', icon: Smile, colorClass: 'text-green-600 dark:text-green-400' },
  neutral: { label: 'Mejorar', icon: Meh, colorClass: 'text-amber-600 dark:text-amber-400' },
  negative: { label: 'Incidente', icon: Frown, colorClass: 'text-red-600 dark:text-red-400' },
};

function getDaysInMonth(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days: Date[] = [];
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d));
  }
  return days;
}

function getCalendarGrid(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startOffset = first.getDay();
  const daysInMonth = last.getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const grid: (Date | null)[] = [];

  for (let i = 0; i < startOffset; i++) {
    grid.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    grid.push(new Date(year, month, d));
  }
  const remaining = totalCells - grid.length;
  for (let i = 0; i < remaining; i++) {
    grid.push(null);
  }
  return grid;
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export function BitacoraHistoryPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { appUser } = useAuth();
  const [notes, setNotes] = useState<BehavioralNote[]>([]);
  const [employeeName, setEmployeeName] = useState('');
  const [department, setDepartment] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const companyId = appUser?.companyId ?? null;

  const load = useCallback(async () => {
    if (!companyId || !employeeId) return;
      setLoading(true);
      setLoadError(null);
      try {
        const empRef = doc(db, 'employees', employeeId);
        const empSnap = await getDoc(empRef);
        if (empSnap.exists()) {
          const data = empSnap.data();
          setEmployeeName(`${data.firstName ?? ''} ${data.lastName ?? ''}`.trim());
          setDepartment(data.department ?? null);
        } else {
          setEmployeeName('Empleado');
        }

        let list: BehavioralNote[] = [];
        try {
          const q = query(
            collection(db, 'behavioral_notes'),
            where('companyId', '==', companyId),
            where('employeeId', '==', employeeId),
            orderBy('createdAt', 'desc')
          );
          const snap = await getDocs(q);
          list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as BehavioralNote));
        } catch (indexErr: unknown) {
          const msg = indexErr instanceof Error ? indexErr.message : String(indexErr);
          if (msg.includes('index') || msg.includes('indexes')) {
            const qFallback = query(
              collection(db, 'behavioral_notes'),
              where('companyId', '==', companyId),
              where('employeeId', '==', employeeId)
            );
            const snap = await getDocs(qFallback);
            list = snap.docs
              .map((d) => ({ id: d.id, ...d.data() } as BehavioralNote))
              .sort((a, b) => {
                const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return tb - ta;
              });
          } else {
            throw indexErr;
          }
        }
        setNotes(list);
      } catch (err) {
        console.error('Error cargando bitácora:', err);
        setLoadError(err instanceof Error ? err.message : 'Error al cargar el historial');
      } finally {
        setLoading(false);
      }
  }, [companyId, employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const notesByDate = notes.reduce<Record<string, BehavioralNote[]>>((acc, n) => {
    const key = n.createdAt ? getDateKeyMexicoCity(n.createdAt) : '';
    if (!acc[key]) acc[key] = [];
    acc[key].push(n);
    return acc;
  }, {});

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const grid = getCalendarGrid(year, month);

  const goPrevMonth = () => setCurrentDate(new Date(year, month - 1));
  const goNextMonth = () => setCurrentDate(new Date(year, month + 1));

  if (!companyId) {
    return (
      <div className="space-y-6">
        <p className="text-muted-foreground">No tienes empresa asignada.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/app/nom035">
            <ChevronLeft className="mr-2 h-4 w-4" />
            Volver a Desarrollo Organizacional
          </Link>
        </Button>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} title="Actualizar">
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de bitácora</CardTitle>
          <CardDescription>
            {employeeName}
            {department && ` · ${department}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Cargando...</p>
          ) : loadError ? (
            <div className="space-y-2 py-8 text-center">
              <p className="text-destructive">{loadError}</p>
              <p className="text-xs text-muted-foreground">
                Si el error menciona &quot;index&quot;, ejecuta: firebase deploy --only firestore:indexes
              </p>
              <Button variant="outline" size="sm" onClick={load}>
                Reintentar
              </Button>
            </div>
          ) : notes.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">No hay registros en la bitácora para este empleado.</p>
              <p className="mt-2 text-xs text-muted-foreground">
                Agrega notas desde la pestaña Bitácora y usa el botón de actualizar si no aparecen.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Vista calendario</h3>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={goPrevMonth}>
                    ←
                  </Button>
                  <span className="min-w-[140px] text-center text-sm font-medium">
                    {MONTH_NAMES[month]} {year}
                  </span>
                  <Button variant="outline" size="sm" onClick={goNextMonth}>
                    →
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {grid.map((date, i) => {
                  if (!date) return <div key={`empty-${i}`} className="min-h-[60px]" />;
                  const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  const dayNotes = notesByDate[key] ?? [];
                  const isCurrentMonth = date.getMonth() === month;

                  return (
                    <div
                      key={key}
                      className={cn(
                        'min-h-[60px] rounded-md border p-1.5',
                        isCurrentMonth ? 'bg-background' : 'bg-muted/30',
                        dayNotes.length > 0 && 'border-primary/50 bg-primary/5'
                      )}
                    >
                      <div className={cn('text-xs font-medium', !isCurrentMonth && 'text-muted-foreground')}>
                        {date.getDate()}
                      </div>
                      <div className="mt-1 space-y-1">
                        {dayNotes.slice(0, 2).map((n) => {
                          const config = TYPE_CONFIG[n.type];
                          const Icon = config.icon;
                          return (
                            <div
                              key={n.id}
                              className={cn(
                                'flex items-start gap-1 rounded px-1 py-0.5 text-xs',
                                config.colorClass
                              )}
                              title={n.note}
                            >
                              <Icon className="mt-0.5 h-3 w-3 shrink-0" />
                              <span className="line-clamp-2">{n.note}</span>
                            </div>
                          );
                        })}
                        {dayNotes.length > 2 && (
                          <div className="text-xs text-muted-foreground">+{dayNotes.length - 2} más</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 border-t pt-4">
                <h3 className="mb-3 text-sm font-semibold">Lista cronológica</h3>
                <div className="space-y-2">
                  {notes.map((n) => {
                    const config = TYPE_CONFIG[n.type];
                    const Icon = config.icon;
                    return (
                      <div
                        key={n.id}
                        className={cn(
                          'flex gap-3 rounded-lg border p-3',
                          n.type === 'positive' && 'border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20',
                          n.type === 'neutral' && 'border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20',
                          n.type === 'negative' && 'border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20'
                        )}
                      >
                        <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', config.colorClass)} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm">{n.note}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {n.createdAt ? formatDateTimeMexicoCity(n.createdAt) : '—'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
